/**
 * The case rail's Hardware section (procurement.md §4): the case's procurement state, each order card (PO status,
 * shipments, receive), and the order builder. Mounted by one line in case-rail-context. A failed lookup says
 * "unreachable; parts status unknown", never "no hardware" (hard rule 10). Owner: web-procurement.
 */
import { Button, ErrorBanner, FactRow, Skeleton } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Link2, Package, PackagePlus } from 'lucide-react'
import * as React from 'react'
import { BooksRecordLink } from '../../components/books-record-link'
import { describeError } from '../../lib/api/client'
import type { CaseView } from '../case-view/api/types'
import { type HardwareOrderOut, isFeatureOff, procurementApi, procurementKeys } from './api'
import { ExistingPurchaseOrderDialog } from './existing-po'
import { OrderBuilder } from './order-builder'
import { CancelledOrders, OrderCard } from './order-card'
import { ProcurementChip } from './status'
import { hardwareHeaderSummary, orderPoLabel, orderStatusLabel, orderTotalLabel } from './summary'

function OrderSummary({ order }: { order: HardwareOrderOut }) {
  const [open, setOpen] = React.useState(false)
  const vendor = order.vendor_name ?? 'Vendor name unavailable'
  const number = orderPoLabel(order)
  const status = orderStatusLabel(order)
  const total = orderTotalLabel(order)
  const panelId = `hardware-order-${order.id}`
  return (
    <div id={`hardware-${order.id}`} data-order={order.id} data-collapsed={open ? 'open' : 'closed'}>
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 rounded-lg border border-border bg-card px-3 py-2 text-sm">
        <button
          type="button"
          className="inline-flex min-w-0 items-center gap-1 text-left font-medium"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((value) => !value)}
        >
          <ChevronRight className={`size-4 shrink-0 text-muted-foreground ${open ? 'rotate-90' : ''}`} aria-hidden />
          <span className="sr-only">
            {open ? 'Collapse' : 'Expand'} hardware order from {vendor}.{' '}
          </span>
          <span className="break-words">{vendor}</span>
        </button>
        <span aria-hidden>·</span>
        <BooksRecordLink href={order.purchase_order?.books_url} label={`Open ${number} in Books`}>
          {number}
        </BooksRecordLink>
        <button
          type="button"
          className="inline-flex min-w-0 flex-wrap items-center gap-x-1.5 text-left text-muted-foreground"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <span aria-hidden>·</span>
          <span>{status}</span>
          {total ? (
            <>
              <span aria-hidden>·</span>
              <span className="tabular">{total}</span>
            </>
          ) : null}
        </button>
      </div>
      {open ? (
        <div id={panelId} className="mt-1">
          <OrderCard order={order} showHeading={false} />
        </div>
      ) : null}
    </div>
  )
}

export function ProcurementSection({ c }: { c: Pick<CaseView, 'uuid'> & { address?: { line: string } } }) {
  const [building, setBuilding] = React.useState(false)
  const [attaching, setAttaching] = React.useState(false)
  const q = useQuery({
    queryKey: procurementKeys.case(c.uuid),
    queryFn: () => procurementApi.caseProcurement(c.uuid),
    retry: 1,
    retryDelay: 300,
  })

  if (q.isPending) return <Skeleton className="my-1.5 h-5 w-full" />
  if (q.isError && isFeatureOff(q.error)) {
    return <FactRow icon={<Package />} label="Hardware" value="Procurement is not switched on in this Patchbay yet" />
  }
  if (q.isError) {
    return (
      <ErrorBanner
        tone="warning"
        title="Procurement unreachable; parts status unknown"
        detail={describeError(q.error)}
        onRetry={() => void q.refetch()}
        className="my-1"
      />
    )
  }
  const data = q.data
  const live = data.orders.filter((o) => o.status !== 'cancelled')
  const cancelled = data.orders.filter((o) => o.status === 'cancelled')
  return (
    <section aria-label="Hardware" className="space-y-2 py-1" data-section="procurement">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Package className="size-4 text-muted-foreground" aria-hidden />
        <span className="text-sm font-medium">Hardware</span>
        {live.length > 1 ? (
          <span className="text-xs text-muted-foreground" data-testid="hardware-summary">
            {hardwareHeaderSummary(live)}
          </span>
        ) : null}
        <ProcurementChip status={data.status} eta={data.eta} />
        {/* Same shape as CRM deal: actions at the right of the title row. They never shrink (#432 clipped
            "Order hardware" to "Orde"); when the rail is too narrow the whole group wraps under the title instead. */}
        <div className="ml-auto flex shrink-0 gap-1" data-testid="hardware-actions">
          <Button size="sm" variant="ghost" className="whitespace-nowrap" onClick={() => setAttaching(true)}>
            <Link2 /> Attach PO
          </Button>
          <Button size="sm" variant="outline" className="whitespace-nowrap" onClick={() => setBuilding(true)}>
            <PackagePlus /> Order hardware
          </Button>
        </div>
      </div>
      {live.length === 0 && cancelled.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hardware ordered on this case.</p>
      ) : null}
      {live.length === 1 ? <OrderCard order={live[0] as HardwareOrderOut} /> : null}
      {live.length > 1 ? live.map((o) => <OrderSummary key={o.id} order={o} />) : null}
      {cancelled.length ? <CancelledOrders orders={cancelled} /> : null}
      <OrderBuilder caseId={c.uuid} open={building} onOpenChange={setBuilding} siteAddress={c.address?.line ?? null} />
      <ExistingPurchaseOrderDialog caseId={c.uuid} open={attaching} onOpenChange={setAttaching} />
    </section>
  )
}
